import { QuantitySelectorUpdateEvent } from '@theme/events';

export function calculateRollArea(length, rollWidth, sectionCount, wastage = 0) {
  if (
    ![length, rollWidth, sectionCount, wastage].every(Number.isFinite) ||
    length <= 0 ||
    rollWidth <= 0 ||
    sectionCount <= 0 ||
    wastage < 0
  ) {
    return null;
  }

  const areaPerSection = length * rollWidth;
  const billableAreaPerSection = Math.ceil(areaPerSection * (1 + wastage / 100) - 1e-9);

  return {
    areaPerSection,
    totalArea: areaPerSection * sectionCount,
    billableAreaPerSection,
    billableArea: billableAreaPerSection * sectionCount,
    wastage,
  };
}

if (typeof HTMLElement !== 'undefined' && !customElements.get('teka-roll-order-calculator')) {
  class TekaRollOrderCalculator extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.addEventListener('input', this.handleInput, { signal });
      this.addEventListener('change', this.handleInput, { signal });
      this.closest('.product-details')?.addEventListener('change', this.handleVariantChange, { signal });
      this.calculate();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    handleInput = (event) => {
      if (!event.target.matches('[data-roll-width], [data-roll-room-length], [data-roll-section-count], [data-roll-wastage]')) return;
      this.calculate();
    };

    handleVariantChange = (event) => {
      const target = event.target;
      const selectedOption = target instanceof HTMLSelectElement ? target.selectedOptions[0] : target;
      if (selectedOption?.dataset.optionName?.toLowerCase() !== 'width') return;

      const rollWidth = Number.parseFloat(selectedOption.value);
      if (!Number.isFinite(rollWidth) || rollWidth <= 0 || rollWidth > 10) return;

      this.dataset.rollWidth = String(rollWidth);
      this.calculate();
    };

    calculate() {
      const widthInput = this.querySelector('[data-roll-width]');
      const lengthInput = this.querySelector('[data-roll-room-length]');
      const sectionCountInput = this.querySelector('[data-roll-section-count]');
      const wastageInput = this.querySelector('[data-roll-wastage]');
      const rollWidth = Number.parseFloat(widthInput?.value ?? this.dataset.rollWidth) || 0;
      const length = Number(lengthInput?.value) || 0;
      const sectionCount = Math.max(1, Math.floor(Number(sectionCountInput?.value) || 1));
      if (sectionCountInput) sectionCountInput.value = String(sectionCount);

      const wastage = wastageInput?.checked ? 10 : 0;
      const price = Number(this.dataset.priceCents) || 0;
      const order = calculateRollArea(length, rollWidth, sectionCount, wastage);
      const totalBillableArea = order?.billableArea || 0;
      const productDetails = this.closest('.product-details');
      const productForm = productDetails?.querySelector('product-form-component');
      const quantityInput =
        productForm?.querySelector('quantity-selector-component input[name="quantity"]') ??
        productForm?.querySelector('input[name="quantity"]') ??
        productDetails?.querySelector('input[name="quantity"]');

      this.dataset.units = String(totalBillableArea);
      this.dataset.roomArea = String(order?.areaPerSection || 0);
      this.dataset.roomLength = String(order ? length : 0);
      this.dataset.roomWidth = String(order ? rollWidth : 0);
      this.dataset.rollCount = String(order ? sectionCount : 0);
      this.dataset.billableArea = String(order?.billableAreaPerSection || 0);
      this.dataset.wastage = String(wastage);

      const areaPerSectionOutput = this.querySelector('[data-roll-area-per-section]');
      const totalAreaOutput = this.querySelector('[data-roll-total-area]');
      const totalOutput = this.querySelector('[data-roll-total]');
      if (areaPerSectionOutput) {
        areaPerSectionOutput.textContent = order ? `${order.areaPerSection.toFixed(2)} m²` : '— m²';
      }
      if (totalAreaOutput) totalAreaOutput.textContent = order ? `${order.totalArea.toFixed(2)} m²` : '— m²';
      if (totalOutput) {
        totalOutput.textContent = order
          ? new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: this.dataset.currency || 'GBP',
            }).format((totalBillableArea * price) / 100)
          : '—';
      }

      const priceNote = this.querySelector('[data-roll-price-note]');
      if (priceNote) {
        priceNote.textContent = order
          ? `${wastage ? 'Includes 10% extra. ' : ''}${totalBillableArea} m² charged at checkout (rounded up to whole m²).`
          : 'Price is calculated per whole m² for checkout.';
      }

      if (quantityInput) {
        quantityInput.value = order ? String(totalBillableArea) : quantityInput.min || '1';
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        const quantitySelector = quantityInput.closest('quantity-selector-component');
        quantitySelector?.updateButtonStates?.();
        if (quantitySelector) {
          quantitySelector.dispatchEvent(new QuantitySelectorUpdateEvent(totalBillableArea));
        }
      }

      const basketButton = productDetails?.querySelector('.add-to-cart-button');
      if (basketButton && !basketButton.disabled) {
        const textNode = [...basketButton.childNodes].find(
          (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim(),
        );
        if (textNode) {
          textNode.textContent = order
            ? `Add ${sectionCount} ${sectionCount === 1 ? 'section' : 'sections'} (${order.totalArea.toFixed(2)} m²) to basket`
            : 'Add to basket';
        }
      }

      const quoteButton = productDetails?.querySelector('[data-room-quote]');
      if (quoteButton) {
        const productTitle = this.dataset.productTitle || '';
        const message = order
          ? `Hi TEKA, please quote ${productTitle}: ${sectionCount} ${sectionCount === 1 ? 'section' : 'sections'}, each ${length} m long with ${rollWidth} m roll width (${order.areaPerSection.toFixed(2)} m² each; ${order.totalArea.toFixed(2)} m² total)${wastage ? ', including 10% extra' : ''}.`
          : `Hi TEKA, please quote ${productTitle}.`;
        quoteButton.href = `${this.dataset.whatsappEndpoint}?text=${encodeURIComponent(message)}`;
      }
    }
  }

  customElements.define('teka-roll-order-calculator', TekaRollOrderCalculator);
}
